import { FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { comicPath, formatDate } from '../lib/format'
import { Comic, ComicLink, PublicComment, RelatedComic } from '../lib/types'
import { Caption, ComicGrid, ComicImages, MetaLine } from './comic'
import { Icon } from './Icon'
import { RandomButton } from './RandomButton'
import { Alert } from './states'

function ComicNav({ comic, previous, next, where }: { comic: Comic; previous: ComicLink | null; next: ComicLink | null; where: string }) {
  return (
    <nav aria-label={`Comic navigation, ${where}`} className="comic-nav">
      {previous ? (
        <Link className="btn solid" to={comicPath(previous.slug)} title={previous.title}>
          <Icon name="left" size={18} />
          Previous
        </Link>
      ) : (
        <button type="button" className="btn solid" disabled>
          <Icon name="left" size={18} />
          Previous
        </button>
      )}
      <RandomButton not={comic.slug} />
      {next ? (
        <Link className="btn solid" to={comicPath(next.slug)} title={next.title}>
          Next
          <Icon name="right" size={18} />
        </Link>
      ) : (
        <button type="button" className="btn solid" disabled>
          Next
          <Icon name="right" size={18} />
        </button>
      )}
    </nav>
  )
}

function Share({ comic }: { comic: Comic }) {
  const [copied, setCopied] = useState(false)
  const url = `${window.location.origin}${comicPath(comic.slug)}`
  useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 3000)
    return () => clearTimeout(t)
  }, [copied])
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      window.prompt('Copy this link:', url)
    }
  }
  return (
    <div className="share">
      <h2>Share</h2>
      <button type="button" className="btn sm" onClick={copy}>
        <Icon name={copied ? 'check' : 'link'} size={18} />
        {copied ? 'Link copied' : 'Copy link'}
      </button>
      <span role="status" className="vh">
        {copied ? 'Link copied to clipboard' : ''}
      </span>
      <a className="btn sm" href={`https://twitter.com/intent/tweet?${new URLSearchParams({ url, text: comic.title })}`} target="_blank" rel="noopener noreferrer">
        X
      </a>
      <a className="btn sm" href={`https://www.facebook.com/sharer/sharer.php?${new URLSearchParams({ u: url })}`} target="_blank" rel="noopener noreferrer">
        Facebook
      </a>
    </div>
  )
}

function Related({ related }: { related: RelatedComic[] }) {
  if (related.length === 0) return null
  return (
    <section aria-labelledby="related-title" className="related">
      <h2 id="related-title" className="h2">
        Related comics
      </h2>
      <ComicGrid comics={related} />
    </section>
  )
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function Comments({ slug, comments }: { slug: string; comments: PublicComment[] }) {
  const [form, setForm] = useState({ name: '', email: '', text: '', website: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle')
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value })

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const errs: Record<string, string> = {}
    if (!form.name.trim()) errs.name = 'Enter your name.'
    if (!EMAIL.test(form.email.trim())) errs.email = 'Enter a valid email address, like name@example.com.'
    if (!form.text.trim()) errs.text = 'Write a comment before posting.'
    setErrors(errs)
    if (Object.keys(errs).length) return
    setState('busy')
    try {
      await api.post(`/api/comics/${encodeURIComponent(slug)}/comments`, form)
      setState('done')
      setForm({ name: '', email: '', text: '', website: '' })
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fields).length) {
        setErrors(error.fields)
        setState('idle')
      } else setState('failed')
    }
  }

  const field = (id: string, key: 'name' | 'email' | 'text', label: string, note?: string) => (
    <div className="formfield">
      <label htmlFor={id} className="lbl">
        {label} {note && <span className="hint">{note}</span>}
      </label>
      {key === 'text' ? (
        <textarea id={id} className={errors.text ? 'field short err' : 'field short'} value={form.text} onChange={set('text')} disabled={state === 'busy'} aria-invalid={Boolean(errors.text)} aria-describedby={errors.text ? `${id}-err` : undefined} />
      ) : (
        <input id={id} className={errors[key] ? 'field err' : 'field'} type={key === 'email' ? 'email' : 'text'} autoComplete={key} value={form[key]} onChange={set(key)} disabled={state === 'busy'} aria-invalid={Boolean(errors[key])} aria-describedby={errors[key] ? `${id}-err` : undefined} />
      )}
      {errors[key] && (
        <div id={`${id}-err`} className="ferr" role="alert">
          {errors[key]}
        </div>
      )}
    </div>
  )

  return (
    <section aria-labelledby="comments-title" className="comments">
      <h2 id="comments-title" className="h2">
        Comments
      </h2>
      {comments.length === 0 ? (
        <p className="lead">No comments yet. Be the first to say something.</p>
      ) : (
        <div>
          {comments.map((c) => (
            <div key={c.id} className="comment">
              <div className="hstack baseline gap-xs-md">
                <strong>{c.name}</strong>
                <span className="hint">{formatDate(c.createdAt)}</span>
              </div>
              <p className="body-text sm">
                {c.text}
              </p>
            </div>
          ))}
        </div>
      )}
      <div className="comment-form">
        <h3 className="h4">
          Leave a comment
        </h3>
        {state === 'done' ? (
          <Alert tone="ok" title="Thanks, your comment was posted." actions={<button type="button" className="btn sm" onClick={() => setState('idle')}>Write another</button>}>
            New comments are reviewed first, so it may take a little while to appear.
          </Alert>
        ) : (
          <form className="form" onSubmit={submit} noValidate>
            {state === 'failed' && <Alert title="Your comment couldn't be posted.">Your text is still in the form. Check your connection and try again.</Alert>}
            {field('c-name', 'name', 'Name')}
            {field('c-email', 'email', 'Email', '(never shown)')}
            {field('c-text', 'text', 'Comment')}
            <input className="vh" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={form.website} onChange={set('website')} />
            <button type="submit" className="btn solid self-start" disabled={state === 'busy'}>
              {state === 'busy' && <span className="spin" aria-hidden="true" />}
              {state === 'busy' ? 'Posting' : state === 'failed' ? 'Try again' : 'Post comment'}
            </button>
            <p className="hint">
              Comments are reviewed before they appear.
            </p>
          </form>
        )}
      </div>
    </section>
  )
}

/** The comic page. In preview (a draft), there's no navigation, sharing or comments. */
export function ComicView({
  comic,
  previous = null,
  next = null,
  related = [],
  comments = [],
  preview = false,
}: {
  comic: Comic
  previous?: ComicLink | null
  next?: ComicLink | null
  related?: RelatedComic[]
  comments?: PublicComment[]
  preview?: boolean
}) {
  return (
    <>
      <article className="post">
        <h1 className="h1 ink mb-sm">
          {comic.title || '[Untitled comic]'}
        </h1>
        <MetaLine date={comic.date} tags={comic.tags} />
        {!preview && <ComicNav comic={comic} previous={previous} next={next} where="top" />}
        <ComicImages comic={comic} where="page" />
        <Caption caption={comic.caption} links={comic.links} />
        {!preview && <ComicNav comic={comic} previous={previous} next={next} where="bottom" />}
        {!preview && <Share comic={comic} />}
      </article>
      {!preview && <Related related={related} />}
      {!preview && (
        <>
          <hr className="post-sep" />
          <Comments slug={comic.slug} comments={comments} />
        </>
      )}
    </>
  )
}
